import { useMemo } from 'react';
import { useExpenseItems } from '../../hooks/useExpenses';
import { useCommissions } from '../../hooks/useCommissions';

interface VendorBalanceSummaryProps {
  vendorId: string;
}

function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Vendor financial summary — shows what the producer still owes this vendor.
 *
 * - **Expense Balance**  Σ of per-item open balance (price − totalPaid) on this
 *   vendor's expenses — money still owed to the vendor.
 * - **Open Commissions** Σ of (amount − paidAmount) on this vendor's commissions
 *   — money the vendor still owes back as commission.
 * - **Total Balance**    Expense Balance − Open Commissions — the net cash still
 *   owed to the vendor once outstanding commissions are netted out.
 */
export function VendorBalanceSummary({ vendorId }: VendorBalanceSummaryProps) {
  const { data: expenses = [] } = useExpenseItems({ vendorId });
  const { data: commissions = [] } = useCommissions({ vendorId });

  const { expenseBalance, openCommissions, totalBalance } = useMemo(() => {
    const expenseBalance = expenses.reduce((sum, item) => sum + item.balance, 0);
    const openCommissions = commissions.reduce((sum, c) => sum + (c.amount - c.paidAmount), 0);
    return {
      expenseBalance,
      openCommissions,
      totalBalance: expenseBalance - openCommissions,
    };
  }, [expenses, commissions]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
      <div className="bg-white rounded-lg shadow px-4 py-3">
        <div className="text-xs text-gray-500">Expense Balance</div>
        <div className="text-lg font-semibold text-gray-900">{formatCurrency(expenseBalance)}</div>
      </div>
      <div className="bg-white rounded-lg shadow px-4 py-3">
        <div className="text-xs text-gray-500">Open Commissions</div>
        <div className="text-lg font-semibold text-gray-900">{formatCurrency(openCommissions)}</div>
      </div>
      <div className="bg-white rounded-lg shadow px-4 py-3 col-span-2 sm:col-span-1">
        <div className="text-xs text-gray-500">Total Balance</div>
        <div
          className={`text-lg font-semibold ${totalBalance > 0 ? 'text-red-600' : 'text-green-600'}`}
        >
          {formatCurrency(totalBalance)}
        </div>
      </div>
    </div>
  );
}
